import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { Box, IconButton, Tooltip } from '@mui/material';
import React from 'react';
import FundOpportunityCardData from '../../models/FundOpportunityCardData';
import ExtendedLiquid from '../../services/ExtendedLiquid';
import LinkifiedHtml from '../LinkifiedHtml';
import { Palette } from 'Palette';
import {
  CARD_BODY_DESCRIPTION_MARGIN_BOTTOM,
  CARD_BODY_PADDING_BOTTOM,
  PROPERTY_CARD_EQUAL_HEIGHT,
} from 'DesignTokens';
import { FONT_INTER, StyledTypography } from '../styles/styles';

/** Description typography shared with the v2 editor's in-place Image Cards body field. */
export const PROPERTY_CARD_BODY_STYLE = {
  fontFamily: FONT_INTER,
  fontSize: '0.875rem',
  lineHeight: 1.5,
  color: '#4F4F4F',
} as const;
import { parseHtmlDescription } from './propertyCardUtils';

interface PropertyCardBodyContentProps {
  mini: boolean;
  processedDescription: string | undefined;
  isHtmlDescription: boolean;
  isExpanded: boolean;
  inPlaceExpanded: boolean;
  cardData: FundOpportunityCardData | undefined;
  actions: React.ReactNode | undefined;
  allocationBreakdown:
    | Array<{
        entityName: string;
        amount: string;
      }>
    | null
    | undefined;
}

export default function PropertyCardBodyContent({
  mini,
  processedDescription,
  isHtmlDescription,
  isExpanded,
  inPlaceExpanded,
  cardData,
  actions,
  allocationBreakdown,
}: PropertyCardBodyContentProps) {
  const descriptionClampSx = {
    display: '-webkit-box',
    WebkitLineClamp: inPlaceExpanded ? 'unset' : 3,
    WebkitBoxOrient: 'vertical' as const,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    mb: mini
      ? CARD_BODY_DESCRIPTION_MARGIN_BOTTOM.mini
      : CARD_BODY_DESCRIPTION_MARGIN_BOTTOM.default,
    fontSize: mini ? '0.85rem' : PROPERTY_CARD_BODY_STYLE.fontSize,
    lineHeight: mini ? 1.4 : PROPERTY_CARD_BODY_STYLE.lineHeight,
    fontFamily: PROPERTY_CARD_BODY_STYLE.fontFamily,
    color: PROPERTY_CARD_BODY_STYLE.color,
    transition: 'all 0.3s ease-in-out',
    maxHeight: inPlaceExpanded ? 'none' : '4.5em',
    '& a.cms-autolink': {
      color: 'inherit',
      textDecoration: 'underline',
    },
  };

  return (
    <Box
      sx={{
        px: mini ? 1.5 : 2,
        pt: mini ? 1 : 1.5,
        pb: mini ? CARD_BODY_PADDING_BOTTOM.mini : CARD_BODY_PADDING_BOTTOM.default,
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        flex: PROPERTY_CARD_EQUAL_HEIGHT ? undefined : 1,
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
      }}
    >
      {isHtmlDescription ? (
        <LinkifiedHtml
          as={StyledTypography}
          className="property-card-description"
          sx={descriptionClampSx}
          component="div"
          html={parseHtmlDescription(processedDescription || '')}
        />
      ) : (
        <StyledTypography
          className="property-card-description"
          sx={{
            ...descriptionClampSx,
            WebkitLineClamp: isExpanded ? 'unset' : 3,
            maxHeight: isExpanded ? 'none' : '4.5em',
          }}
          component="div"
        >
          {processedDescription}
        </StyledTypography>
      )}
      {cardData?.investment !== undefined && (
        <Box
          sx={{
            mt: 1,
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
          }}
        >
          <StyledTypography
            sx={{
              fontFamily: 'Inter, sans-serif',
              color: '#4F4F4F',
            }}
          >
            {cardData.investmentLabel}: {ExtendedLiquid.currencyMillions(cardData.investment, true)}
          </StyledTypography>
          {!!allocationBreakdown?.length && (
            <Tooltip
              arrow
              placement="top-end"
              title={
                <Box sx={{ minWidth: 260 }}>
                  <Box
                    sx={{
                      fontWeight: 700,
                      textDecoration: 'underline',
                      mb: 0.5,
                    }}
                  >
                    Allocated investment
                  </Box>
                  {allocationBreakdown.map((row) => (
                    <Box
                      key={row.entityName}
                      sx={{
                        display: 'grid',
                        gridTemplateColumns: 'minmax(0, 1fr) auto auto',
                        columnGap: 0,
                        alignItems: 'baseline',
                        width: '100%',
                      }}
                    >
                      <Box
                        sx={{
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          minWidth: 0,
                          pr: '0.5rem',
                        }}
                      >
                        {row.entityName}
                      </Box>
                      <Box>$</Box>
                      <Box
                        sx={{
                          textAlign: 'right',
                          fontVariantNumeric: 'tabular-nums',
                        }}
                      >
                        {row.amount}
                      </Box>
                    </Box>
                  ))}
                </Box>
              }
              slotProps={{
                tooltip: {
                  sx: {
                    backgroundColor: Palette.tone4.background,
                    color: Palette.tone4.text,
                    borderRadius: '8px',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                    fontSize: '0.75rem',
                    lineHeight: 1.35,
                    maxWidth: 360,
                    p: '8px 12px',
                  },
                },
                arrow: {
                  sx: {
                    color: Palette.tone4.background,
                  },
                },
              }}
            >
              <IconButton
                size="small"
                sx={{
                  p: 0,
                  ml: 0.25,
                  color: 'inherit',
                  cursor: 'help',
                  py: 0,
                }}
                aria-label="View allocation breakdown"
              >
                <InfoOutlinedIcon />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      )}
      {actions && <Box>{actions}</Box>}
    </Box>
  );
}
